import React, { useEffect, useState } from "react";
import api from "@api";
import "./EditUserProfile.css";

function EditUserProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [allSports, setAllSports] = useState([]);
  const [mySports, setMySports] = useState([]);
  const [selectedSportIds, setSelectedSportIds] = useState([]);
  const [sportDetails, setSportDetails] = useState({});
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    bio: "",
    city: "",
    state: "",
  });

  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState("");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await api.get("/api/user-profiles-v2/me", {
          withCredentials: true,
        });

        const currentProfile = res.data.profile;

        setProfile(currentProfile);

        setFormData({
          first_name: currentProfile?.first_name || "",
          last_name: currentProfile?.last_name || "",
          bio: currentProfile?.bio || "",
          city: currentProfile?.city || "",
          state: currentProfile?.state || "",
        });

        setProfileImagePreview(
          currentProfile?.profile_image || ""
        );
      } catch (error) {
        console.error("Failed to fetch profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchSports = async () => {
      try {
        const [allSportsRes, mySportsRes] = await Promise.all([
          api.get("/api/sports-catalog"),
          api.get("/api/user-profile-sports/me"),
        ]);

        const availableSports =
          allSportsRes.data.sports || [];

        const currentSports =
          mySportsRes.data.sports || [];

        setAllSports(availableSports);
        setMySports(currentSports);
        const details = {};

        currentSports.forEach((item) => {
          details[Number(item.sport_id)] = {
            skill_level: item.skill_level || "",
            rating_type: item.rating_type || "",
            rating: item.rating || "",
            position: item.position || "",
            looking_to_play: item.looking_to_play || false,
          };
        });

        setSportDetails(details);
        setSelectedSportIds(
          currentSports.map((item) =>
            Number(item.sport_id)
          )
        );
      } catch (error) {
        console.error("Failed to fetch sports:", error);
      }
    };

    fetchSports();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }

    setError("");
    setProfileImage(file);

    const previewUrl = URL.createObjectURL(file);
    setProfileImagePreview(previewUrl);
  };

  const handleToggleSport = (sportId) => {
    const id = Number(sportId);

    setSelectedSportIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter(
          (selectedId) => selectedId !== id
        );
      }

      return [...prev, id];
    });
  };

  const handleSportDetailChange = (sportId, field, value) => {
    const id = Number(sportId);

    setSportDetails((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  const getSportConfig = (sportName) => {
    const name = sportName.toLowerCase();

    if (name === "tennis") {
      return {
        rating: true,
        positions: [],
      };
    }

    if (name === "pickleball") {
      return {
        rating: true,
        positions: [],
      };
    }

    if (name === "basketball") {
      return {
        rating: false,
        positions: [
          "Point Guard",
          "Shooting Guard",
          "Small Forward",
          "Power Forward",
          "Center",
        ],
      };
    }

    if (
      name === "flag football" ||
      name === "football"
    ) {
      return {
        rating: false,
        positions: [
          "Quarterback",
          "Running Back",
          "Wide Receiver",
          "Center",
          "Rusher",
          "Defensive Back",
        ],
      };
    }

    if (name === "soccer") {
      return {
        rating: false,
        positions: [
          "Goalkeeper",
          "Defender",
          "Midfielder",
          "Forward",
        ],
      };
    }

    if (name === "baseball" || name === "softball") {
      return {
        rating: false,
        positions: [
          "Pitcher",
          "Catcher",
          "First Base",
          "Second Base",
          "Third Base",
          "Shortstop",
          "Outfield",
        ],
      };
    }

    if (name === "volleyball") {
      return {
        rating: false,
        positions: [
          "Setter",
          "Outside Hitter",
          "Opposite",
          "Middle Blocker",
          "Libero",
          "Defensive Specialist",
        ],
      };
    }

    return {
      rating: false,
      positions: [],
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (
      !formData.first_name.trim() ||
      !formData.last_name.trim()
    ) {
      setError(
        "First name and last name are required."
      );
      return;
    }

    try {
      setIsSubmitting(true);

      // --------------------------------
      // SAVE BASIC PROFILE
      // --------------------------------

      const data = new FormData();

      data.append(
        "first_name",
        formData.first_name.trim()
      );

      data.append(
        "last_name",
        formData.last_name.trim()
      );

      data.append(
        "display_name",
        `${formData.first_name.trim()} ${formData.last_name.trim()}`
      );

      data.append("bio", formData.bio);
      data.append("city", formData.city);
      data.append("state", formData.state);

      if (profileImage) {
        data.append("profile_image", profileImage);
      }

      const res = await api.put(
        "/api/user-profiles-v2/me",
        data,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // --------------------------------
      // SAVE SPORTS
      // --------------------------------

      const savedSportIds = mySports.map((item) =>
        Number(item.sport_id)
      );

      const sportsToAdd = selectedSportIds.filter(
        (sportId) =>
          !savedSportIds.includes(sportId)
      );

      const sportsToRemove = savedSportIds.filter(
        (sportId) =>
          !selectedSportIds.includes(sportId)
      );

      for (const sportId of sportsToAdd) {
        const details = sportDetails[sportId] || {};

        await api.post(
          "/api/user-profile-sports",
          {
            sport_id: sportId,
            skill_level: details.skill_level || null,
            rating_type: details.rating_type || null,
            rating: details.rating || null,
            position: details.position || null,
            looking_to_play: details.looking_to_play || false,
          }
        );
      }
      const sportsToUpdate = selectedSportIds.filter(
        (sportId) => savedSportIds.includes(sportId)
      );

      for (const sportId of sportsToUpdate) {
        const details = sportDetails[sportId] || {};

        await api.put(
          `/api/user-profile-sports/${sportId}`,
          {
            skill_level: details.skill_level || null,
            rating_type: details.rating_type || null,
            rating: details.rating || null,
            position: details.position || null,
            looking_to_play: details.looking_to_play || false,
          }
        );
      }


      for (const sportId of sportsToRemove) {
        await api.delete(
          `/api/user-profile-sports/${sportId}`
        );
      }

      // --------------------------------
      // REFRESH SAVED SPORTS
      // --------------------------------

      const updatedSportsRes = await api.get(
        "/api/user-profile-sports/me"
      );

      const updatedSports =
        updatedSportsRes.data.sports || [];

      setMySports(updatedSports);

      setSelectedSportIds(
        updatedSports.map((item) =>
          Number(item.sport_id)
        )
      );

      // --------------------------------
      // UPDATE PROFILE STATE
      // --------------------------------

      setProfile(res.data.profile);

      if (res.data.profile?.profile_image) {
        setProfileImagePreview(
          res.data.profile.profile_image
        );
      }

      setProfileImage(null);

      alert("Profile updated!");
    } catch (error) {
      console.error(
        "Failed to update profile:",
        error
      );

      setError(
        error.response?.data?.message ||
        "Failed to update your profile."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="edit-user-profile-page">
        Loading profile...
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="edit-user-profile-page">
        Profile not found.
      </div>
    );
  }

  return (
    <div className="edit-user-profile-page">
      <div className="edit-user-profile-card">

        <div className="edit-user-profile-header">
          <p className="edit-user-profile-eyebrow">
            SportingHip Profile
          </p>

          <h1>Edit Profile</h1>

          <p>
            Update the information shown on your
            public sports profile.
          </p>
        </div>

        {error && (
          <div className="edit-user-profile-alert error">
            {error}
          </div>
        )}

        <form
          className="edit-user-profile-form"
          onSubmit={handleSubmit}
        >

          {/* Profile Image */}
          <div className="edit-user-profile-image-section">

            <div className="edit-user-profile-avatar">
              {profileImagePreview ? (
                <img
                  src={profileImagePreview}
                  alt="Profile"
                />
              ) : (
                <span>
                  {formData.first_name?.charAt(0)}
                  {formData.last_name?.charAt(0)}
                </span>
              )}
            </div>

            <div className="edit-user-profile-field">
              <label htmlFor="profile_image">
                Profile Photo
              </label>

              <input
                type="file"
                id="profile_image"
                accept="image/*"
                onChange={handleImageChange}
              />

              <small>
                Choose a photo from your device.
              </small>
            </div>

          </div>

          {/* First + Last */}
          <div className="edit-user-profile-row">

            <div className="edit-user-profile-field">
              <label htmlFor="first_name">
                First Name
              </label>

              <input
                type="text"
                id="first_name"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </div>

            <div className="edit-user-profile-field">
              <label htmlFor="last_name">
                Last Name
              </label>

              <input
                type="text"
                id="last_name"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </div>

          </div>

          {/* City + State */}
          <div className="edit-user-profile-row">

            <div className="edit-user-profile-field">
              <label htmlFor="city">
                City
              </label>

              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
              />
            </div>

            <div className="edit-user-profile-field">
              <label htmlFor="state">
                State
              </label>

              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
              />
            </div>

          </div>

          {/* My Sports */}
          <div className="edit-user-profile-field">
            <label>My Sports</label>

            <small>
              Choose the sports you play or want to
              play.
            </small>

            <div className="edit-user-profile-sports">
              {allSports.map((sport) => {
                const isSelected =
                  selectedSportIds.includes(
                    Number(sport.id)
                  );

                return (
                  <button
                    key={sport.id}
                    type="button"
                    className={`edit-user-profile-sport ${isSelected ? "selected" : ""
                      }`}
                    onClick={() =>
                      handleToggleSport(sport.id)
                    }
                  >
                    {isSelected && <span>✓</span>}
                    {sport.name}
                  </button>
                );
              })}
            </div>
          </div>
          {/* Sport Details */}
          {selectedSportIds.length > 0 && (
            <div className="edit-user-profile-sport-details">

              {allSports
                .filter((sport) =>
                  selectedSportIds.includes(Number(sport.id))
                )
                .map((sport) => {
                  const sportId = Number(sport.id);
                  const config = getSportConfig(sport.name);
                  const details = sportDetails[sportId] || {};

                  return (
                    <div
                      key={sport.id}
                      className="edit-user-profile-sport-detail-card"
                    >
                      <div className="edit-user-profile-sport-detail-header">
                        <h3>{sport.name}</h3>

                        <span>Player Details</span>
                      </div>

                      <div className="edit-user-profile-sport-detail-fields">

                        {/* LEVEL */}
                        <div className="edit-user-profile-field">
                          <label>Level</label>

                          <select
                            value={details.skill_level || ""}
                            onChange={(e) =>
                              handleSportDetailChange(
                                sportId,
                                "skill_level",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Choose level</option>
                            <option value="Beginner">Beginner</option>
                            <option value="Intermediate">
                              Intermediate
                            </option>
                            <option value="Advanced">
                              Advanced
                            </option>
                          </select>
                        </div>

                        {/* TENNIS / PICKLEBALL RATING */}
                        {config.rating && (
                          <div className="edit-user-profile-field">
                            <label>Rating</label>

                            <select
                              value={details.rating || ""}
                              onChange={(e) =>
                                handleSportDetailChange(
                                  sportId,
                                  "rating",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">
                                No rating / Not sure
                              </option>

                              <option value="2.0">2.0</option>
                              <option value="2.5">2.5</option>
                              <option value="3.0">3.0</option>
                              <option value="3.5">3.5</option>
                              <option value="4.0">4.0</option>
                              <option value="4.5">4.5</option>
                              <option value="5.0">5.0</option>
                              <option value="5.5+">5.5+</option>
                            </select>
                          </div>
                        )}

                        {/* TEAM SPORT POSITION */}
                        {config.positions.length > 0 && (
                          <div className="edit-user-profile-field">
                            <label>Position</label>

                            <select
                              value={details.position || ""}
                              onChange={(e) =>
                                handleSportDetailChange(
                                  sportId,
                                  "position",
                                  e.target.value
                                )
                              }
                            >
                              <option value="">Choose position</option>

                              {config.positions.map((position) => (
                                <option
                                  key={position}
                                  value={position}
                                >
                                  {position}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="edit-user-profile-field">
                          <label className="edit-user-profile-checkbox-label">
                            <input
                              type="checkbox"
                              checked={details.looking_to_play || false}
                              onChange={(e) =>
                                handleSportDetailChange(
                                  sportId,
                                  "looking_to_play",
                                  e.target.checked
                                )
                              }
                            />

                            Open to play
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                  
                })}
                
            </div>
            
          )}
          
          {/* Bio */}
          <div className="edit-user-profile-field">
            <label htmlFor="bio">
              Bio
            </label>

            <textarea
              id="bio"
              name="bio"
              value={formData.bio}
              onChange={handleChange}
              rows="5"
              maxLength={500}
              placeholder="Tell people a little about yourself and the sports you enjoy."
            />

            <small>
              {formData.bio.length}/500 characters
            </small>
          </div>

          <div className="edit-user-profile-actions">
            <button
              type="submit"
              className="edit-user-profile-save"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Saving..."
                : "Save Changes"}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}

export default EditUserProfile;